/**
 * ZAPHUB 2.1 — Compliance Report Page
 *
 * SOC2 + GDPR compliance report generated from AuditLog data.
 * Admin/Nardiha only. Print-friendly (window.print()).
 */

import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Shield,
  FileText,
  CheckCircle,
  AlertCircle,
  Printer,
  Download,
  Loader2,
  RefreshCw,
  Users,
  Activity,
  Lock,
} from 'lucide-react';
import api from '../services/apiClient';

interface ComplianceReport {
  generatedAt: string;
  generatedBy: string;
  period: { from: string; to: string; days: number };
  platform: { name: string; version: string; url: string };
  soc2: Record<string, { label: string; status: string; details: string }>;
  gdpr: Record<string, { label: string; status: string; details: string }>;
  activity: {
    totalEvents: number;
    authEvents: number;
    failedLogins: number;
    dataAccessEvents: number;
    adminActions: number;
    deletions: number;
    copilotUsage: number;
    workflowExecutions: number;
    topActiveUsers: Array<{ id: string; name: string; email: string; role: string; eventCount: number }>;
  };
  riskScore: number;
  riskLabel: string;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'PASS') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle className="w-3 h-3 mr-1" /> PASS
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
      <AlertCircle className="w-3 h-3 mr-1" /> REVIEW
    </Badge>
  );
}

export function ComplianceReportPage() {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');

  const generateReport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/audit/compliance-report?days=${days}`);
      setReport(data.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadJSON = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zaphub-compliance-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 p-6 print:p-0">
      {/* Header — hidden in print */}
      <div className="flex items-start justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Report</h1>
          <p className="text-gray-500 mt-1">SOC2 Type II · GDPR · Audit Trail</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Periodo:</label>
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              <option value={7}>7 días</option>
              <option value={30}>30 días</option>
              <option value={90}>90 días</option>
              <option value={365}>1 año</option>
            </select>
          </div>
          <Button
            onClick={generateReport}
            disabled={loading}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
            ) : (
              <><Shield className="w-4 h-4 mr-2" /> Generar Informe</>
            )}
          </Button>
          {report && (
            <>
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
              <Button variant="outline" onClick={handleDownloadJSON}>
                <Download className="w-4 h-4 mr-2" /> JSON
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <Card className="p-4 bg-red-50 border-red-200 print:hidden">
          <div className="flex gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        </Card>
      )}

      {!report && !loading && (
        <Card className="p-12 text-center text-gray-400 print:hidden">
          <Shield className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-lg">Selecciona el periodo y genera el informe de compliance</p>
          <p className="text-sm mt-2">Cubre SOC2 Trust Services Criteria + GDPR Articles</p>
        </Card>
      )}

      {report && (
        <div className="space-y-6">
          {/* Cover / Title block */}
          <Card className="p-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{report.platform.name} — Compliance Report</h2>
                <p className="text-blue-200">Version {report.platform.version} · {report.platform.url}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-blue-200 text-xs uppercase tracking-wide">Periodo</div>
                <div className="font-semibold">{new Date(report.period.from).toLocaleDateString('es-ES')} — {new Date(report.period.to).toLocaleDateString('es-ES')}</div>
              </div>
              <div>
                <div className="text-blue-200 text-xs uppercase tracking-wide">Generado por</div>
                <div className="font-semibold">{report.generatedBy}</div>
              </div>
              <div>
                <div className="text-blue-200 text-xs uppercase tracking-wide">Riesgo Global</div>
                <div className={`font-bold text-xl ${report.riskLabel === 'LOW' ? 'text-green-300' : 'text-yellow-300'}`}>
                  {report.riskLabel} ({report.riskScore.toFixed(0)}/100)
                </div>
              </div>
            </div>
          </Card>

          {/* SOC2 */}
          <Card className="overflow-hidden">
            <div className="p-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-blue-900">SOC2 Trust Service Criteria</h3>
              </div>
            </div>
            <div className="divide-y">
              {Object.values(report.soc2).map((criterion) => (
                <div key={criterion.label} className="flex items-start justify-between p-4">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">{criterion.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{criterion.details}</div>
                  </div>
                  <StatusBadge status={criterion.status} />
                </div>
              ))}
            </div>
          </Card>

          {/* GDPR */}
          <Card className="overflow-hidden">
            <div className="p-4 bg-green-50 border-b border-green-100">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-900">GDPR — Reglamento General de Protección de Datos</h3>
              </div>
            </div>
            <div className="divide-y">
              {Object.values(report.gdpr).map((article) => (
                <div key={article.label} className="flex items-start justify-between p-4">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">{article.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{article.details}</div>
                  </div>
                  <StatusBadge status={article.status} />
                </div>
              ))}
            </div>
          </Card>

          {/* Activity Summary */}
          <Card className="overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-gray-600" />
                <h3 className="font-bold text-gray-900">Resumen de Actividad — {report.period.days} días</h3>
              </div>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Eventos totales', value: report.activity.totalEvents, color: 'text-blue-700' },
                { label: 'Eventos de autenticación', value: report.activity.authEvents, color: 'text-indigo-700' },
                { label: 'Intentos fallidos de login', value: report.activity.failedLogins, color: report.activity.failedLogins > 5 ? 'text-red-600' : 'text-green-600' },
                { label: 'Accesos a datos', value: report.activity.dataAccessEvents, color: 'text-purple-700' },
                { label: 'Acciones admin', value: report.activity.adminActions, color: 'text-orange-700' },
                { label: 'Eliminaciones', value: report.activity.deletions, color: report.activity.deletions > 20 ? 'text-red-600' : 'text-gray-700' },
                { label: 'Uso AI Copilot', value: report.activity.copilotUsage, color: 'text-pink-700' },
                { label: 'Ejecuciones workflows', value: report.activity.workflowExecutions, color: 'text-teal-700' },
              ].map(stat => (
                <div key={stat.label} className="text-center p-3 bg-gray-50 rounded-xl">
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Active Users */}
          {report.activity.topActiveUsers.length > 0 && (
            <Card className="overflow-hidden">
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  <h3 className="font-bold text-gray-900">Usuarios más activos</h3>
                </div>
              </div>
              <div className="divide-y">
                {report.activity.topActiveUsers.slice(0, 8).map((u, i) => (
                  <div key={u.id || i} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-xs">{u.role}</Badge>
                      <span className="text-sm font-semibold text-gray-700">{u.eventCount} eventos</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Footer */}
          <div className="text-center text-xs text-gray-400 py-4">
            Generado el {new Date(report.generatedAt).toLocaleString('es-ES')} ·
            ZAPHUB {report.platform.version} · {report.platform.url}
          </div>
        </div>
      )}
    </div>
  );
}
